import { supabase } from "@/integrations/supabase/client";

export interface SocialComment {
  id: string;
  post_id: string;
  user_id: string;
  content: string;
  created_at: string;
  profile?: {
    id: string;
    username: string | null;
    full_name: string | null;
    avatar_url: string | null;
    avatar_background: string | null;
  } | null;
}

const attachProfiles = async (rows: Omit<SocialComment, "profile">[]): Promise<SocialComment[]> => {
  if (rows.length === 0) return [];
  const ids = Array.from(new Set(rows.map((r) => r.user_id)));
  const { data: profiles } = await supabase
    .from("user_profiles")
    .select("id, username, full_name, avatar_url, avatar_background")
    .in("id", ids);

  const byId = new Map((profiles ?? []).map((p) => [p.id, p]));
  return rows.map((row) => ({ ...row, profile: byId.get(row.user_id) ?? null }));
};

export const getSocialComments = async (postId: string): Promise<SocialComment[]> => {
  const { data, error } = await supabase
    .from("social_post_comments")
    .select("id, post_id, user_id, content, created_at")
    .eq("post_id", postId)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return attachProfiles((data ?? []) as Omit<SocialComment, "profile">[]);
};

export const addSocialComment = async (
  postId: string,
  userId: string,
  content: string,
): Promise<SocialComment> => {
  const { data, error } = await supabase
    .from("social_post_comments")
    .insert({ post_id: postId, user_id: userId, content: content.trim() })
    .select("id, post_id, user_id, content, created_at")
    .single();

  if (error) throw error;
  const [withProfile] = await attachProfiles([data as Omit<SocialComment, "profile">]);
  return withProfile ?? ({ ...(data as Omit<SocialComment, "profile">), profile: null } as SocialComment);
};

export const deleteSocialComment = async (commentId: string): Promise<void> => {
  const { error } = await supabase.from("social_post_comments").delete().eq("id", commentId);
  if (error) throw error;
};
